import { Form } from 'react-bootstrap';
import styles from './ActionItemChecklist.module.scss';
import { useEffect, useMemo, useRef, useState } from 'react';
import { loader } from 'graphql.macro';
import { useMutation, useQuery } from '@apollo/client';
import { Text } from '../typography';
import UserSelector from '../UserSelector';
import Icon from '../Icon';
import { customColors } from '../../utilities';
import { useCurrentUser } from '../../providers/UserProvider';
import TextWithIcon from '../typography/TextWithIcon';
import { fromNowDateOnly } from '../../utilities/time';
import { LinearProgress } from '@mui/material';
import Button from '../Button';
import { statusStringLookup } from '../action_items/action_item_utilities';
import { useTranslation } from 'react-i18next';
import { keys } from '../../utilities/translator/translation_keys';
import { useActionItem } from '../../hooks/action_items/update_action_item';
import { pastDue } from '../../utilities/audits';
import { useIsMobile } from '../../hooks/misc';
import Spacer from '../typography/Spacer';
import { useWorkspace } from '../../providers/WorkspaceProvider';

const addActionItemChecklistMutation = loader(
  './ActionItemChecklist.add.graphql',
);
const updateActionItemChecklistMutation = loader(
  './ActionItemChecklist.update.graphql',
);
const actionItemChecklistQuery = loader('./ActionItemChecklist.fetch.graphql');
const deleteActionItemChecklistMutation = loader(
  './ActionItemChecklist.delete.graphql',
);
const fetchActionItemEvents = loader('./ActionItemModal.events.graphql');

export default function ActionItemChecklist({ actionItem, readOnly }) {
  const { isWorkspaceAdmin } = useWorkspace();
  const [state, setState] = useState({});
  const [items, setItems] = useState({});
  const [showAddChecklist, setShowAddChecklist] = useState(false);
  const [showSubItems, setShowSubItems] = useState(null);
  const [currentValue, setCurrentValue] = useState('');
  const [editingChecklist, setEditingChecklist] = useState(null);
  const [editing, setEditing] = useState(null);
  const [editingSubitemId, setEditingSubitemId] = useState(null);
  const [editingValue, setEditingValue] = useState('');
  const { user } = useCurrentUser();
  const isMobile = useIsMobile();
  const [addActionItemChecklist] = useMutation(addActionItemChecklistMutation);
  const [updateActionItemChecklist] = useMutation(
    updateActionItemChecklistMutation,
  );
  const [deleteActionItemChecklist] = useMutation(
    deleteActionItemChecklistMutation,
  );

  const { updateActionItem, addParticipant } = useActionItem();
  const { id: parentId, creator } = actionItem || {};
  const [expand, setExpand] = useState(false);
  const containerRef = useRef();
  const { t } = useTranslation();
  const { refetch: refetchEvents } = useQuery(fetchActionItemEvents, {
    variables: { id: `${actionItem.id}` },
  });

  useEffect(() => {
    if (!!readOnly) {
      setEditingChecklist(null);
      setShowAddChecklist(false);
      setShowSubItems(null);
      setEditing(null);
      setShowSubItems(null);
    }
  }, [readOnly]);

  const { refetch, data: { actionItemChecklist = [] } = {} } = useQuery(
    actionItemChecklistQuery,
    {
      skip: !parentId,
      variables: { parentId, id: `${parentId}` },
      onCompleted: ({ actionItemChecklist }) => {
        const itemsByChecklist = (actionItemChecklist || []).reduce(
          (acc, checklist) => {
            acc[checklist.id] = checklist.items || [];
            return acc;
          },
          {},
        );
        setItems(itemsByChecklist);
        refetchEvents();
      },
    },
  );
  useEffect(() => {
    async function handleClickOutside(event) {
      const pendingDesc = state[editingChecklist];
      if (
        !!editingChecklist &&
        typeof pendingDesc === 'string' &&
        !!pendingDesc &&
        actionItemChecklist?.some((cl) => cl.id === editingChecklist)
      ) {
        await updateActionItemChecklist({
          variables: {
            id: editingChecklist,
            description: pendingDesc,
          },
        });
      }

      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setEditingChecklist(null);
        setShowAddChecklist(false);
        setShowSubItems(null);
        setEditing(null);
        setShowSubItems(null);
      }
    }
    if (editingChecklist) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actionItemChecklist?.length, editingChecklist, showAddChecklist, state]);

  function handleSubitemChange({ type, checklistId, item, status }) {
    setItems((prev) => {
      const current = prev[checklistId] || [];
      let next = [];

      if (type === 'add') {
        next = [...current, item];
      } else if (type === 'edit') {
        next = current?.map((sub) => (sub.id === item.id ? item : sub));
      } else if (type === 'delete') {
        next = current?.filter((sub) => sub.id !== item.id);
      } else if (type === 'status') {
        next = current?.map((sub) =>
          sub.id === item.id ? { ...sub, status: status } : sub,
        );
      }
      const update = { ...prev };
      if (next.length) {
        update[checklistId] = next;
      } else {
        delete update[checklistId];
      }
      updateActionItemChecklist({
        variables: { id: checklistId, items: next },
      });
      return update;
    });
  }

  async function addChecklistItem() {
    const data = await addActionItemChecklist({
      variables: { ...state, parentId },
    });
    setState({ ...state, description: null });
    setEditing(data?.addActionItemChecklist?.id);
    refetch();
  }

  async function editChecklistDesc(checklistId, value) {
    await updateActionItemChecklist({
      variables: { id: checklistId, description: value },
    });
    setEditing(null);
    refetch();
  }
  const overflow = useMemo(() => {
    const itemLength = Object.values(items).reduce(
      (acc, arr) => acc + arr.length,
      0,
    );
    return actionItemChecklist?.length + itemLength > 7;
  }, [actionItemChecklist?.length, items]);

  const checklistProgress = useMemo(() => {
    if (!actionItemChecklist || actionItemChecklist?.length === 0) return 0;
    const scores = actionItemChecklist?.map((cl) => {
      const items = Array.isArray(cl.items) ? cl.items : [];
      if (items.length === 0) {
        return cl.status === 'DONE' ? 1 : 0;
      } else {
        const subDone = items.filter((i) => i.status === 'DONE').length;
        return subDone / items.length;
      }
    });
    const totalScore = scores.reduce((a, b) => a + b, 0);
    return Math.round((totalScore / actionItemChecklist.length) * 100);
  }, [actionItemChecklist]);

  function getColor(percent) {
    if (percent <= 20) return customColors.RED_LIGHT;
    if (percent <= 40) return '#FFBCB3';
    if (percent <= 60) return customColors.YELLOW_LIGHT;
    if (percent <= 80) return customColors.GREEN_ULTRA_LIGHT;
    if (percent < 100) return customColors.GREEN_LIGHT;
    if (percent === 100) return customColors.GREEN;
    return '#0288D1';
  }
  const barColor = getColor(checklistProgress);

  useEffect(() => {
    if (checklistProgress > 0 && actionItem?.status === 'TODO') {
      updateActionItem({ actionItemId: actionItem?.id, status: 'IN_PROGRESS' });
    }
  }, [actionItem?.id, actionItem?.status, checklistProgress, updateActionItem]);

  const checkReadOnly = ({ assignee }) =>
    assignee?.id !== user?.id && creator?.id !== user?.id && !isWorkspaceAdmin;

  return (
    <div>
      <div className={styles.header}>
        <div className={styles.flexRow}>
          <Text noMargin weight="semiBold">
            Checklist
          </Text>
          {actionItemChecklist?.length ? (
            <Text
              weight="semiBold"
              color="secondary"
              noMargin
            >{`${checklistProgress}%`}</Text>
          ) : null}
        </div>
        {!readOnly && (
          <Icon
            hover
            style={{
              color: showAddChecklist ? customColors.GREEN : customColors.BLUE,
            }}
            onClick={() => {
              setShowAddChecklist(!showAddChecklist);
              setShowSubItems(null);
              setEditing(null);
            }}
          >
            {showAddChecklist ? 'check' : 'add'}
          </Icon>
        )}
      </div>
      {actionItemChecklist?.length ? (
        <div className={styles.progress}>
          <LinearProgress
            variant="determinate"
            sx={{
              height: 12,
              borderRadius: 1,
              backgroundColor: '#f5f5f5',
              '& .MuiLinearProgress-bar': {
                backgroundColor: barColor,
                transition: 'background-color .4s',
              },
            }}
            value={checklistProgress}
          />
        </div>
      ) : null}
      <div
        ref={containerRef}
        className={
          !expand && !showAddChecklist && !showSubItems
            ? styles.container
            : styles.expandedContainer
        }
      >
        {showAddChecklist && (
          <div className={styles.input}>
            {!isMobile ? <Icon>task_alt</Icon> : null}
            <Form.Control
              placeholder="Enter New Task"
              as="textarea"
              rows={1}
              value={state.description || ''}
              onChange={(e) =>
                setState({ ...state, description: e.target.value })
              }
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  addChecklistItem();
                }
              }}
            />
            <Button
              disabled={!state.description}
              value="Add"
              icon="add"
              size="sm"
              onClick={addChecklistItem}
            />
          </div>
        )}
        {actionItemChecklist?.map(
          ({
            description,
            id: checklistId,
            dueDate,
            status,
            assignee,
            completer,
            dateCompleted,
          }) => {
            const selectedSubItem = showSubItems === checklistId;
            const selectedChecklist = editing === checklistId;
            const editingSubItemChecklist = editingChecklist === checklistId;
            return (
              <div
                className={
                  editingSubItemChecklist || selectedSubItem
                    ? styles.checklistContainer
                    : null
                }
                key={checklistId}
              >
                <div
                  className={
                    status === 'DONE' ? styles.flexRow : styles.checklist
                  }
                >
                  <div className={styles.left}>
                    <div className={styles.flexRow}>
                      {actionItem.status === 'DONE' ? (
                        <Icon
                          style={{
                            color:
                              status === 'DONE'
                                ? customColors.GREEN_LIGHT
                                : customColors.RED,
                          }}
                        >
                          {status === 'DONE' ? 'check_circle' : 'close'}
                        </Icon>
                      ) : (
                        <Form.Check
                          disabled={
                            checkReadOnly({ assignee }) ||
                            items[checklistId]?.length
                          }
                          checked={status === 'DONE'}
                          onChange={(e) => {
                            const s = status === 'TODO' ? 'DONE' : 'TODO';
                            e.preventDefault();
                            e.stopPropagation();
                            updateActionItemChecklist({
                              variables: {
                                id: checklistId,
                                status: s,
                              },
                            });
                          }}
                        />
                      )}
                      {selectedChecklist ? (
                        <Form.Control
                          className={styles.text}
                          value={state[checklistId]}
                          onChange={(e) =>
                            setState({
                              ...state,
                              [checklistId]: e.target.value,
                            })
                          }
                          onKeyDown={(e) => {
                            if (
                              e.key === 'Enter' &&
                              !e.shiftKey &&
                              !!e.target.value
                            ) {
                              e.preventDefault();
                              editChecklistDesc(checklistId, e.target.value);
                            }
                          }}
                          onBlur={(e) => {
                            if (!!e.target.value) {
                              setState({
                                ...state,
                                [checklistId]: e.target.value,
                              });
                              editChecklistDesc(checklistId, e.target.value);
                            }
                          }}
                        />
                      ) : (
                        <div
                          style={{ width: '100%' }}
                          onClick={() => {
                            if (!readOnly && status !== 'DONE') {
                              setState({
                                ...state,
                                [checklistId]: description,
                              });
                              setEditingSubitemId(null);
                              setEditing(checklistId);
                              setEditingChecklist(checklistId);
                              setShowSubItems(null);
                            }
                          }}
                        >
                          <Text
                            noMargin
                            hover={!readOnly && status !== 'DONE'}
                            className={styles.text}
                            size={!description ? 'sm' : 'md'}
                            color={!description ? 'secondaryLight' : 'primary'}
                          >
                            {description || 'Click to add description'}
                          </Text>
                        </div>
                      )}
                    </div>
                  </div>
                  {selectedChecklist ? (
                    <div className={styles.flexRowXS}>
                      <Icon
                        style={{ color: customColors.GREEN }}
                        onClick={() => {
                          setEditing(null);
                          setEditingChecklist(null);
                        }}
                      >
                        check
                      </Icon>
                      <Icon
                        hover
                        style={{ color: 'red', fontSize: '1.2rem' }}
                        onClick={() => {
                          deleteActionItemChecklist({
                            variables: { id: checklistId },
                          }).then(() => {
                            setEditing(null);
                            setEditingChecklist(null);
                            refetch();
                          });
                        }}
                      >
                        delete
                      </Icon>
                    </div>
                  ) : readOnly ? (
                    completer || assignee ? (
                      <img
                        className={styles.avatar}
                        alt="avatar"
                        src={completer?.avatarUrl ?? assignee?.avatarUrl}
                      />
                    ) : null
                  ) : (
                    <div className={styles.flexRowXSRight}>
                      <div
                        className={
                          status === 'DONE'
                            ? styles.hide
                            : selectedChecklist || selectedSubItem
                            ? styles.flexRowXS
                            : styles.toolbar
                        }
                      >
                        <Icon
                          hover
                          style={{
                            fontSize: '1.4rem',
                            color: selectedSubItem
                              ? customColors.GREEN
                              : customColors.BLUE,
                          }}
                          onClick={() => {
                            setShowSubItems(
                              showSubItems === checklistId ? null : checklistId,
                            );
                            setShowAddChecklist(false);
                            setEditing(null);
                          }}
                        >
                          {showSubItems === checklistId ? 'check' : 'add'}
                        </Icon>
                        {!showSubItems && (
                          <div>
                            <UserSelector
                              modalClassName={styles.userModal}
                              userQuery={
                                !!actionItem.workspace?.id ? 'workspace' : 'all'
                              }
                              selected={null}
                              otherWorkspace={actionItem.workspace || null}
                              iconDisplay={true}
                              onClose={() => {
                                setState({
                                  ...state,
                                  [checklistId]: {
                                    ...state[checklistId],
                                    assignee: null,
                                  },
                                });
                                updateActionItemChecklist({
                                  variables: {
                                    id: checklistId,
                                    assigneeId: null,
                                  },
                                });
                              }}
                              onSelectionChanged={(assignee) => {
                                setState({
                                  ...state,
                                  [checklistId]: { assignee },
                                });
                                updateActionItemChecklist({
                                  variables: {
                                    id: checklistId,
                                    assigneeId: assignee.id,
                                  },
                                }).then(() => {
                                  const isParticipant =
                                    actionItem.participants?.some(
                                      (u) => u.id === assignee.id,
                                    );
                                  if (!isParticipant) {
                                    addParticipant({
                                      actionItemId: actionItem.id,
                                      userId: assignee.id,
                                    });
                                  }
                                  refetch();
                                });
                              }}
                            />
                          </div>
                        )}
                        {!showSubItems && (
                          <div
                            style={{
                              display: dateCompleted ? 'none' : 'flex',
                              alignItems: 'center',
                              position: 'relative',
                              width: '1.6em',
                              height: '1.6em',
                              justifyContent: 'center',
                            }}
                          >
                            <Icon
                              hover
                              style={{
                                fontSize: '1.2rem',
                                color: customColors.BLUE,
                                lineHeight: 1,
                                pointerEvents: 'none',
                              }}
                            >
                              access_time
                            </Icon>
                            <input
                              type="date"
                              onChange={(e) => {
                                const dueDate =
                                  e.target.value?.toString() || null;
                                updateActionItemChecklist({
                                  variables: {
                                    id: checklistId,
                                    dueDate,
                                  },
                                });
                                refetch();
                              }}
                              style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                opacity: 0,
                                cursor: 'pointer',
                                zIndex: 2,
                                margin: 0,
                                padding: 0,
                                border: 'none',
                              }}
                              tabIndex={0}
                            />
                          </div>
                        )}
                      </div>
                      {completer?.id ? (
                        <img
                          className={styles.avatar}
                          alt="avatar"
                          src={completer.avatarUrl}
                        />
                      ) : (
                        <div className={styles.flexRowXSRight}>
                          {!!dueDate && (
                            <div className={styles.label}>
                              <div className={styles.flexRowXS}>
                                <Icon
                                  style={{
                                    fontSize: '1rem',
                                    color: !!pastDue(dueDate)
                                      ? customColors.RED_LIGHT
                                      : customColors.SECONDARY,
                                  }}
                                >
                                  timer
                                </Icon>
                                <Text
                                  size="sm"
                                  noMargin
                                  color={!!pastDue(dueDate) ? 'red' : 'primary'}
                                >
                                  {fromNowDateOnly(dueDate)}
                                </Text>
                              </div>
                            </div>
                          )}
                          {!!assignee && (
                            <div className={styles.label}>
                              <img
                                src={assignee.avatarUrl}
                                alt="avatar"
                                className={styles.avatar}
                              />
                              {!isMobile && status !== 'DONE' ? (
                                <Icon
                                  className={
                                    isMobile
                                      ? styles.hide
                                      : styles.showCloseOnHover
                                  }
                                  hover
                                  style={{ fontSize: '1rem' }}
                                  onClick={() => {
                                    setState({
                                      ...state,
                                      [checklistId]: {
                                        ...state[checklistId],
                                        assignee: null,
                                      },
                                    });
                                    updateActionItemChecklist({
                                      variables: {
                                        id: checklistId,
                                        assigneeId: null,
                                      },
                                    });
                                  }}
                                >
                                  delete
                                </Icon>
                              ) : null}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <div>
                  {showSubItems === checklistId && (
                    <div className={styles.subItemInput}>
                      <Icon>playlist_add</Icon>
                      <Form.Control
                        className={styles.formSmall}
                        value={currentValue ?? ''}
                        placeholder="Enter New Subtask"
                        onChange={(e) => setCurrentValue(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            const newSubitem = {
                              value: e.target.value,
                              id: Date.now(),
                              creatorId: user.id,
                              status: 'TODO',
                            };
                            handleSubitemChange({
                              type: 'add',
                              checklistId,
                              item: newSubitem,
                            });
                            setCurrentValue('');
                          }
                        }}
                      />
                      <Button
                        value="Add"
                        disabled={!currentValue}
                        size="sm"
                        icon="add"
                        onClick={() => {
                          const newSubitem = {
                            value: currentValue,
                            id: Date.now(),
                            creatorId: user.id,
                            status: 'TODO',
                          };
                          handleSubitemChange({
                            type: 'add',
                            checklistId,
                            item: newSubitem,
                          });
                          setCurrentValue('');
                        }}
                      />
                    </div>
                  )}
                  {!!items[checklistId] &&
                    items[checklistId]?.map((item) => {
                      const show = editingSubitemId === item?.id;
                      return (
                        <div className={styles.subItem} key={item.id}>
                          <div className={styles.left}>
                            <div className={styles.flexRowXS}>
                              {actionItem.status === 'DONE' ? (
                                <Icon
                                  style={{
                                    color:
                                      item.status === 'DONE'
                                        ? customColors.GREEN_LIGHT
                                        : customColors.RED_LIGHT,
                                    fontSize: '1.2rem',
                                  }}
                                >
                                  {item.status === 'DONE'
                                    ? 'check_circle'
                                    : 'cancel'}
                                </Icon>
                              ) : (
                                <Form.Check
                                  disabled={readOnly}
                                  size="small"
                                  checked={item.status === 'DONE'}
                                  onChange={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    const currentStatus = item.status || 'TODO';
                                    const newStatus =
                                      currentStatus === 'TODO'
                                        ? 'DONE'
                                        : 'TODO';

                                    handleSubitemChange({
                                      type: 'status',
                                      checklistId,
                                      item,
                                      status: newStatus,
                                    });
                                    if (newStatus === 'TODO') {
                                      setState({ ...state, status: 'TODO' });

                                      updateActionItemChecklist({
                                        variables: {
                                          id: checklistId,
                                          status: 'TODO',
                                        },
                                      });
                                    }
                                    const subitems = items[checklistId] || [];
                                    const updatedSubitems = subitems?.map(
                                      (subitem) =>
                                        subitem.id === item.id
                                          ? { ...subitem, status: newStatus }
                                          : subitem,
                                    );
                                    const allDone =
                                      updatedSubitems.length > 0 &&
                                      updatedSubitems.every(
                                        (i) => i.status === 'DONE',
                                      );
                                    const checklistStatusShouldBe = allDone
                                      ? 'DONE'
                                      : 'TODO';
                                    if (
                                      state.status !== checklistStatusShouldBe
                                    ) {
                                      setState({
                                        ...state,
                                        status: checklistStatusShouldBe,
                                      });
                                      updateActionItemChecklist({
                                        variables: {
                                          id: checklistId,
                                          status: checklistStatusShouldBe,
                                        },
                                      });
                                    }
                                  }}
                                />
                              )}
                              {show && !readOnly ? (
                                <Form.Control
                                  className={styles.text}
                                  value={editingValue}
                                  autoFocus
                                  onChange={(e) =>
                                    setEditingValue(e.target.value)
                                  }
                                  onBlur={() => {
                                    handleSubitemChange({
                                      type: 'edit',
                                      checklistId,
                                      item: { ...item, value: editingValue },
                                    });
                                    setEditingSubitemId(null);
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                      e.preventDefault();
                                      e.target.blur();
                                    }
                                  }}
                                />
                              ) : (
                                <div
                                  style={{ width: '100%' }}
                                  onClick={() => {
                                    if (!readOnly) {
                                      setEditing(null);
                                      setEditingSubitemId(item.id);
                                      setEditingChecklist(checklistId);
                                      setEditingValue(item.value);
                                      setShowSubItems(false);
                                    }
                                  }}
                                >
                                  <Text
                                    noMargin
                                    hover={!readOnly && status !== 'DONE'}
                                    className={styles.text}
                                    size={!item.value ? 'xs' : 'sm'}
                                    color={
                                      !item.value ? 'secondaryLight' : 'primary'
                                    }
                                  >
                                    {item.value || 'Click to add description'}
                                  </Text>
                                </div>
                              )}
                            </div>
                          </div>
                          {!readOnly && item.status !== 'DONE' && (
                            <div className={show ? undefined : styles.toolbar}>
                              <Icon
                                hover
                                style={{ fontSize: '1rem', color: 'red' }}
                                onClick={(e) => {
                                  e.preventDefault();
                                  handleSubitemChange({
                                    type: 'delete',
                                    checklistId,
                                    item,
                                  });
                                }}
                              >
                                delete
                              </Icon>
                            </div>
                          )}
                        </div>
                      );
                    })}
                </div>
              </div>
            );
          },
        )}

        {!readOnly &&
          checklistProgress === 100 &&
          !actionItem?.assignee?.id && (
            <div>
              <Spacer />
              <Button
                size="sm"
                align={'right'}
                disabled={checklistProgress !== 100}
                value={t(keys.common.COMPLETE)}
                icon="check_circle"
                variant="success"
                onClick={() => {
                  updateActionItem({
                    actionItemId: actionItem.id,
                    status: statusStringLookup.DONE.key,
                  });
                }}
              />
            </div>
          )}
      </div>

      {!!overflow && !showAddChecklist && (
        <div>
          <Spacer />
          <TextWithIcon
            hover
            icon={!!expand ? 'expand_less' : 'expand_more'}
            textAlign="right"
            size="sm"
            iconProps={{ fontSize: '1.2rem' }}
            weight="semiBold"
            noMargin
            color="accentPrimary"
            onClick={() => {
              setExpand(!expand);
            }}
          >
            {!!expand ? 'Show Less' : 'Show More'}
          </TextWithIcon>
        </div>
      )}
    </div>
  );
}
